import { useState } from 'react';
import type { FormEvent } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import {
  Bug,
  Layers,
  LayoutTemplate,
  Lightbulb,
  MessageCircle,
  Send,
  Sparkles,
  X,
  type LucideIcon,
} from 'lucide-react';

import { ApiError } from '../lib/api';
import { cn } from '../lib/cn';
import { useFeedbackConfig, useSubmitFeedback, type FeedbackType } from '../lib/feedback';
import { t, type MessageKey } from '../lib/i18n';
import { Button, FormError, TextArea } from './ui';

/** Se cierra por la sesión (sessionStorage); vuelve a aparecer en la próxima. */
const DISMISS_KEY = 'tembiapo_feedback_banner_dismissed';

const TYPES: { value: FeedbackType; labelKey: MessageKey; icon: LucideIcon }[] = [
  { value: 'SUGGESTION', labelKey: 'feedback.type.suggestion', icon: Lightbulb },
  { value: 'BUG', labelKey: 'feedback.type.bug', icon: Bug },
  { value: 'VERTICAL', labelKey: 'feedback.type.vertical', icon: Layers },
  { value: 'TEMPLATE', labelKey: 'feedback.type.template', icon: LayoutTemplate },
  { value: 'OTHER', labelKey: 'feedback.type.other', icon: MessageCircle },
];

/**
 * Banner + modal de feedback de lanzamiento (temporal). Se muestra sólo cuando el
 * backend habilita la recolección (`/feedback/config` → enabled). El banner se
 * puede cerrar por la sesión; el modal deja enviar sugerencias/bugs.
 *
 * NO se renderiza en modo embebido (isla nativa): el marco nativo tiene su propio
 * canal. Tono marca/sun, 100% tokens (theme-aware).
 */
export function FeedbackLaunch() {
  const { data } = useFeedbackConfig();
  const submit = useSubmitFeedback();

  const [dismissed, setDismissed] = useState<boolean>(() => {
    try {
      return sessionStorage.getItem(DISMISS_KEY) === '1';
    } catch {
      return false;
    }
  });

  const [open, setOpen] = useState(false);
  const [type, setType] = useState<FeedbackType>('SUGGESTION');
  const [message, setMessage] = useState('');
  const [fieldError, setFieldError] = useState('');
  const [topError, setTopError] = useState('');
  const [doneType, setDoneType] = useState<FeedbackType | null>(null);

  // Sólo si el backend lo habilita explícitamente.
  if (data?.enabled !== true) return null;

  function reset() {
    setType('SUGGESTION');
    setMessage('');
    setFieldError('');
    setTopError('');
    setDoneType(null);
  }

  function handleOpenChange(next: boolean) {
    if (!next) reset();
    setOpen(next);
  }

  function dismissBanner() {
    setDismissed(true);
    try {
      sessionStorage.setItem(DISMISS_KEY, '1');
    } catch {
      /* modo privado: vale sólo para esta pestaña */
    }
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setTopError('');
    setFieldError('');

    if (!message.trim()) {
      setFieldError(t('common.required'));
      return;
    }

    try {
      await submit.mutateAsync({
        type,
        message: message.trim(),
        context: window.location.pathname,
        source: 'WEB',
      });
      setDoneType(type);
    } catch (err) {
      if (err instanceof ApiError && (err.status === 429 || err.code === 'too_many')) {
        setTopError(t('feedback.error.tooMany'));
      } else if (err instanceof ApiError && err.code === 'validation_error') {
        setFieldError(err.fields?.message ?? t('common.required'));
      } else {
        setTopError(t('common.genericError'));
      }
    }
  };

  return (
    <Dialog.Root open={open} onOpenChange={handleOpenChange}>
      {!dismissed && (
        <div className="mb-6 flex flex-col gap-3 rounded-token border border-brand/30 bg-brand/10 px-4 py-3 sm:flex-row sm:items-center">
          <Sparkles size={18} aria-hidden className="shrink-0 text-brand" />
          <p className="min-w-0 flex-1 text-sm text-ink">{t('feedback.banner.title')}</p>
          <div className="flex shrink-0 items-center gap-1.5">
            <Dialog.Trigger asChild>
              <Button type="button" className="h-8 gap-1.5 px-3 text-sm">
                <Send size={15} aria-hidden />
                {t('feedback.banner.cta')}
              </Button>
            </Dialog.Trigger>
            <button
              type="button"
              onClick={dismissBanner}
              aria-label={t('common.close')}
              className="grid h-8 w-8 place-items-center rounded-token text-muted transition-colors hover:bg-surface-2 hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand"
            >
              <X size={16} aria-hidden />
            </button>
          </div>
        </div>
      )}

      <Dialog.Portal>
        <Dialog.Overlay className="overlay-in fixed inset-0 z-40 bg-ink/50 backdrop-blur-[2px]" />
        <Dialog.Content
          aria-describedby={undefined}
          className="fixed left-1/2 top-1/2 z-50 max-h-[90vh] w-[calc(100%-2rem)] max-w-md -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-token border border-line bg-surface shadow-lift focus:outline-none"
        >
          <div className="flex items-center justify-between border-b border-line px-5 py-4">
            <Dialog.Title className="font-display text-lg font-semibold text-ink">
              {doneType ? t('feedback.done.title') : t('feedback.modal.title')}
            </Dialog.Title>
            <Dialog.Close
              className="grid h-8 w-8 place-items-center rounded-token text-muted transition-colors hover:bg-surface-2 hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand"
              aria-label={t('common.close')}
            >
              <X size={18} />
            </Dialog.Close>
          </div>

          {doneType ? (
            <div className="space-y-5 px-5 py-6">
              <div className="flex flex-col items-center gap-3 text-center">
                <span className="grid h-12 w-12 place-items-center rounded-full bg-brand/10 text-brand">
                  <Sparkles size={24} aria-hidden />
                </span>
                <p className="text-sm text-muted">
                  {doneType === 'BUG' ? t('feedback.done.bug') : t('feedback.done.idea')}
                </p>
              </div>
              <div className="flex justify-end">
                <Button type="button" onClick={() => handleOpenChange(false)}>
                  {t('common.close')}
                </Button>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4 px-5 py-5" noValidate>
              <FormError>{topError}</FormError>

              <div className="space-y-2">
                <span className="block text-sm font-medium text-ink">
                  {t('feedback.modal.title')}
                </span>
                <div className="flex flex-wrap gap-2">
                  {TYPES.map((ty) => {
                    const Icon = ty.icon;
                    const active = type === ty.value;
                    return (
                      <button
                        key={ty.value}
                        type="button"
                        onClick={() => setType(ty.value)}
                        aria-pressed={active}
                        className={cn(
                          'inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm font-medium transition-colors',
                          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand',
                          active
                            ? 'border-brand bg-brand/10 text-brand'
                            : 'border-line text-muted hover:border-brand hover:text-ink',
                        )}
                      >
                        <Icon size={15} aria-hidden />
                        {t(ty.labelKey)}
                      </button>
                    );
                  })}
                </div>
                <p className="text-xs text-muted">{t('feedback.disclaimer')}</p>
              </div>

              <TextArea
                label={t('feedback.message.label')}
                name="message"
                rows={4}
                placeholder={t('feedback.message.placeholder')}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                error={fieldError}
              />

              <div className="flex items-center justify-end gap-2 pt-1">
                <Button type="button" variant="secondary" onClick={() => handleOpenChange(false)}>
                  {t('common.cancel')}
                </Button>
                <Button type="submit" loading={submit.isPending}>
                  <Send size={16} aria-hidden />
                  {t('feedback.submit')}
                </Button>
              </div>
            </form>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
